package org.example.project.enginefinanzen

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.enginepersonal.PersonalverwaltungStyles
import org.example.project.manager.LumaticaApiManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private object AbgabenApi {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = false }

    suspend fun load(): All = runCatching {
        json.decodeFromString(ListSerializer(All.AllNames.serializer()), LumaticaApiManager.call("finanzdashboard/abgaben"))
            .firstOrNull { it.id == "finanz_abgaben" }
            ?: All(id = "finanz_abgaben", bezeichnung = "Abgabeeinstellungen")
    }.getOrElse { All(id = "finanz_abgaben", bezeichnung = "Abgabeeinstellungen") }

    suspend fun save(settings: All): All? = runCatching {
        json.decodeFromString(
            All.AllNames.serializer(),
            LumaticaApiManager.postJson(
                "finanzdashboard/abgabe-save",
                json.encodeToString(All.AllNames.serializer(), settings)
            )
        )
    }.getOrNull()
}

@Composable
fun SysAbgabeeinstellungenScreen() {
    val scope = rememberCoroutineScope()
    var settings by remember { mutableStateOf(All(id = "finanz_abgaben", bezeichnung = "Abgabeeinstellungen")) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        settings = AbgabenApi.load()
        loading = false
    }

    fun save() {
        if (saving) return
        scope.launch {
            saving = true
            val saved = AbgabenApi.save(settings.copy(id = "finanz_abgaben"))
            if (saved != null) settings = saved
            message = if (saved != null) "Abgabeeinstellungen gespeichert." else "Speichern fehlgeschlagen."
            saving = false
        }
    }

    Style(PersonalverwaltungStyles)
    Div({ classes(PersonalverwaltungStyles.page) }) {
        Div({ classes(PersonalverwaltungStyles.shell) }) {
            Div({ classes(PersonalverwaltungStyles.topbar) }) {
                Div({ classes(PersonalverwaltungStyles.employeeHeader) }) {
                    Div({ classes(PersonalverwaltungStyles.employeeText) }) {
                        H3({ classes(PersonalverwaltungStyles.title) }) { Text("Abgabeeinstellungen") }
                    }
                }
                Div({ classes(PersonalverwaltungStyles.userSelect) }) {}
                LumaticaButton(
                    label = if (saving) "Speichern..." else "Speichern",
                    onClick = ::save,
                    variant = LumaticaButtonVariant.Action,
                    enabled = !loading && !saving,
                    heightPx = 36
                )
            }

            if (message.isNotBlank()) {
                Div({ style { color(Color("#d8dee8")); fontSize(13.px) } }) { Text(message) }
            }

            Div({ classes(PersonalverwaltungStyles.content) }) {
                Div({ classes(PersonalverwaltungStyles.akteSurface) }) {
                    Div({ classes(PersonalverwaltungStyles.sectionBlock) }) {
                        H3({ classes(PersonalverwaltungStyles.sectionTitle) }) { Text("Abgaben") }

                        AbgabenGruppe("Steuern") {
                            Div { Text("Einkommensteuer: automatisch aus dem Gewinn (Splittingtarif 2026)") }
                            AbgabenFeld("Gewerbesteuer-Hebesatz (%)", settings.gewerbesteuerHebesatz.ifBlank { "417" }) {
                                settings = settings.copy(gewerbesteuerHebesatz = it)
                            }
                        }

                        AbgabenGruppe("Versicherungen") {
                            AbgabenFeld("Krankenkasse ohne Krankengeld (%)", settings.krankenkasseProzent.ifBlank { "16.9" }) {
                                settings = settings.copy(krankenkasseProzent = it)
                            }
                            AbgabenFeld("Pflegeversicherung (%)", settings.pflegeversicherungProzent.ifBlank { "3.6" }) {
                                settings = settings.copy(pflegeversicherungProzent = it)
                            }
                            AbgabenFeld("Kirchensteuer (%)", settings.kirchensteuerProzent.ifBlank { "9" }) {
                                settings = settings.copy(kirchensteuerProzent = it)
                            }
                            AbgabenFeld("Rentenversicherung monatlich (€)", settings.rentenversicherungMonatlich) {
                                settings = settings.copy(rentenversicherungMonatlich = it)
                            }
                        }

                        AbgabenGruppe("Weitere Abgaben") {
                            AbgabenFeld("Sonstige Abgaben monatlich (€)", settings.sonstigeAbgabenMonatlich) {
                                settings = settings.copy(sonstigeAbgabenMonatlich = it)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AbgabenGruppe(title: String, content: @Composable () -> Unit) {
    Div({ classes(PersonalverwaltungStyles.groupBlock) }) {
        Div({ classes(PersonalverwaltungStyles.groupTitle) }) { Text(title) }
        Div({ classes(PersonalverwaltungStyles.fieldGrid) }) { content() }
    }
}

@Composable
private fun AbgabenFeld(label: String, currentValue: String, onChange: (String) -> Unit) {
    Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
        Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text(label) }
        Input(InputType.Text, attrs = {
            classes(PersonalverwaltungStyles.input)
            value(currentValue)
            onInput { onChange(it.value) }
        })
    }
}
